import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Target, ArrowRightCircle, Sparkles, Trash2, RefreshCw, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PRIORITY_COUNTRIES, HIDDEN_COUNTRIES } from "@/lib/countries";
import { useAuth } from "@/lib/auth";
import Flag from "@/components/Flag";
import ClientGroupCombobox from "@/components/ClientGroupCombobox";
import ClientContactsManager from "@/components/ClientContactsManager";
import IconAction from "@/components/IconAction";

const EMPTY = {
  name: "", group_name: "", address: "", ice: "", country: "MA",
  currency: "MAD", billing_type: "TVA_20",
  contact_gender: "", contact_name: "", contact_email: "", notes: "",
};

const SIM_TONE = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  SENT: "bg-blue-100 text-blue-800",
  WON: "bg-emerald-100 text-emerald-800",
  LOST: "bg-red-100 text-red-800",
  CANCELLED: "bg-zinc-100 text-zinc-500",
};

export default function Prospects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'new' | prospect | null
  const [form, setForm] = useState(EMPTY);
  const [countries, setCountries] = useState([]);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isCEO = user?.role === "CEO";

  const load = () =>
    api.get("/prospects")
      .then((r) => setItems(r.data?.prospects || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/holidays/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);

  const onCountryChange = (code) => {
    const isMA = code === "MA";
    setForm((p) => ({ ...p, country: code, currency: isMA ? "MAD" : "EUR", billing_type: isMA ? "TVA_20" : "HT" }));
  };

  const open = (mode) => {
    if (mode === "new") { setForm(EMPTY); setDialog("new"); return; }
    setForm({
      name: mode.name || "", group_name: mode.group_name || "", address: mode.address || "",
      ice: mode.ice || "", country: mode.country || "MA", currency: mode.currency || "MAD",
      billing_type: mode.billing_type || "TVA_20", contact_gender: mode.contact_gender || "",
      contact_name: mode.contact_name || "", contact_email: mode.contact_email || "",
      notes: mode.notes || "",
    });
    setDialog(mode);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom du prospect est obligatoire."); return; }
    setSaving(true);
    try {
      if (dialog === "new") {
        await api.post("/prospects", form);
        toast.success("Prospect créé");
      } else {
        const { contact_name, contact_email, contact_gender, ...rest } = form;
        await api.patch(`/prospects/${dialog.client_id}`, rest);
        toast.success("Prospect mis à jour");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const convert = async (p) => {
    try {
      await api.post(`/prospects/${p.client_id}/convert-to-client`);
      toast.success(`« ${p.name} » est désormais un client`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/prospects/${p.client_id}`);
      toast.success("Prospect supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const runMigration = async () => {
    try {
      const r = await api.post("/prospects/migrate");
      const n = r.data?.migrated_count ?? 0;
      toast.success(n ? `${n} prospect(s) rapatrié(s) depuis les simulations existantes` : "Aucun prospect à rapatrier");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      `${p.name || ""} ${p.group_name || ""} ${p.ice || ""} ${p.contact_name || ""}`.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="prospects-page">
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Pipeline</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Prospects</h1>
          <p className="text-zinc-500 mt-3 max-w-xl text-sm md:text-base">
            Toute simulation ouverte vit ici. Dès que la mission est gagnée, le prospect bascule
            automatiquement dans la base Clients.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isCEO && (
            <IconAction icon={RefreshCw} size="lg" testid="prospects-migrate-btn" label="Rapatrier l'existant : crée les prospects à partir des anciennes simulations" onClick={runMigration} />
          )}
          <button onClick={() => open("new")} data-testid="new-prospect-btn"
            className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
            <Plus size={14} /> Nouveau prospect
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-zinc-200 rounded-lg p-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Recherche</Label>
          <Input data-testid="prospects-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, groupe, ICE, contact…" className="h-9" />
        </div>
        <div className="text-[11px] font-mono text-zinc-500 self-center" data-testid="prospects-count">
          {filtered.length} / {items.length}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-3">Prospect</div>
          <div className="col-span-2">Groupe / Holding</div>
          <div className="col-span-1">ICE</div>
          <div className="col-span-1">Pays</div>
          <div className="col-span-2">Contact</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {loading && <div className="px-5 py-16 text-sm text-zinc-500 text-center">Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center" data-testid="prospects-empty">
            Aucun prospect. Créez-en un, ou lancez une simulation avec un nouveau prospect.
          </div>
        )}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">Aucun prospect ne correspond à la recherche.</div>
        )}
        {filtered.map((p) => (
          <div key={p.client_id} className="block md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-3 md:py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
            data-testid={`prospect-row-${p.client_id}`}>
            <div className="col-span-3 flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Target size={15} /></div>
              <div className="min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {(p.simulations || []).length === 0 && (
                    <span className="text-[10px] font-mono text-zinc-400">aucune simulation</span>
                  )}
                  {(p.simulations || []).map((s) => (
                    <span key={s.simulation_id}
                      data-testid={`prospect-sim-${s.simulation_id}`}
                      title={s.title}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono ${SIM_TONE[s.status] || "bg-zinc-100"}`}>
                      <Sparkles size={9} /> {s.status}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden md:block md:col-span-2 text-sm text-zinc-700 truncate">{p.group_name || <span className="text-zinc-400">—</span>}</div>
            <div className="hidden md:block md:col-span-1 text-xs font-mono text-zinc-600 truncate">{p.ice || <span className="text-zinc-400">—</span>}</div>
            <div className="hidden md:block md:col-span-1 text-xs font-mono uppercase text-zinc-700">
              {p.country ? <span className="inline-flex items-center gap-1"><Flag code={p.country} size={14} alt={false} />{p.country}</span> : <span className="text-zinc-400">—</span>}
            </div>
            <div className="hidden md:block md:col-span-2 text-xs text-zinc-600 truncate">
              {p.contact_name ? `${p.contact_gender || ""} ${p.contact_name}`.trim() : <span className="text-zinc-400">—</span>}
              {p.contact_email && <div className="text-[10px] font-mono text-zinc-400 truncate">{p.contact_email}</div>}
            </div>
            <div className="col-span-3 flex md:justify-end items-center gap-2 mt-3 md:mt-0">
              {p.drive_folder_url && (
                <IconAction icon={ExternalLink} href={p.drive_folder_url} target="_blank" testid={`prospect-drive-${p.client_id}`} label="Ouvrir le dossier Google Drive du prospect" />
              )}
              <IconAction icon={Pencil} testid={`edit-prospect-${p.client_id}`} label="Éditer le prospect et ses contacts" onClick={() => open(p)} />
              {isCEO && (
                <IconAction icon={ArrowRightCircle} tone="success" testid={`convert-prospect-${p.client_id}`} label="Convertir en client (bascule dans la base Clients)" onClick={() => convert(p)} />
              )}
              {isCEO && (p.simulations || []).length === 0 && (
                <IconAction icon={Trash2} tone="danger" testid={`delete-prospect-${p.client_id}`} label="Supprimer le prospect" onClick={() => remove(p)} />
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
          <DialogHeader>
            <DialogTitle>{dialog === "new" ? "Nouveau prospect" : `Éditer ${dialog?.name}`}</DialogTitle>
            <DialogDescription>
              Mêmes informations qu'un client — elles seront reprises telles quelles sur les devis
              puis sur les factures une fois la mission gagnée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nom <span className="text-red-600">*</span></Label>
              <Input data-testid="prospect-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div><Label>Groupe / Holding (optionnel)</Label>
              <ClientGroupCombobox value={form.group_name} onChange={(v) => setForm({ ...form, group_name: v })} testid="prospect-field-group" />
            </div>
            <div><Label>Adresse</Label>
              <Textarea data-testid="prospect-field-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, ville, pays" />
            </div>
            <div><Label>ICE</Label>
              <Input data-testid="prospect-field-ice" value={form.ice} onChange={(e) => setForm({ ...form, ice: e.target.value })} placeholder="Identifiant fiscal" />
            </div>
            <div>
              <Label>Pays</Label>
              <Select value={form.country || ""} onValueChange={onCountryChange}>
                <SelectTrigger data-testid="prospect-field-country"><SelectValue placeholder="Choisir un pays" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(() => {
                    const byCode = Object.fromEntries(countries.map((c) => [c.code, c]));
                    const priority = PRIORITY_COUNTRIES.map((code) => byCode[code]).filter(Boolean);
                    const rest = countries
                      .filter((c) => !PRIORITY_COUNTRIES.includes(c.code) && !HIDDEN_COUNTRIES.has(c.code))
                      .sort((a, b) => a.name.localeCompare(b.name));
                    return (
                      <>
                        {priority.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                          </SelectItem>
                        ))}
                        {!showAllCountries && (
                          <button type="button" data-testid="prospect-country-show-more"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAllCountries(true); }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="w-full text-left py-1.5 pl-8 pr-2 text-sm text-zinc-500 font-medium hover:bg-zinc-100 rounded-sm">
                            Autre…
                          </button>
                        )}
                        {showAllCountries && rest.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                          </SelectItem>
                        ))}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Devise</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="prospect-field-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Facturation</Label>
                <Select value={form.billing_type} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                  <SelectTrigger data-testid="prospect-field-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HT">HT (sans TVA)</SelectItem>
                    <SelectItem value="TVA_20">TTC (TVA 20%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {dialog && dialog !== "new" ? (
              <div className="border-t border-zinc-100 pt-3">
                <ClientContactsManager clientId={dialog.client_id} />
              </div>
            ) : (
            <div className="border-t border-zinc-100 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Contact prospect</div>
              <div className="grid grid-cols-[90px_1fr] gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Civilité</Label>
                  <select data-testid="prospect-field-contact-gender" value={form.contact_gender}
                    onChange={(e) => setForm({ ...form, contact_gender: e.target.value })}
                    className="w-full h-10 border border-zinc-200 rounded px-2 text-sm bg-white">
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme.">Mme.</option>
                    <option value="M./Mme">M./Mme</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Nom contact</Label>
                  <Input data-testid="prospect-field-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-[10px] uppercase text-zinc-500">Email contact</Label>
                <Input data-testid="prospect-field-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </div>
            </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="prospect-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contexte commercial, prochaine action…" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="prospect-save"
              className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md disabled:opacity-50">
              {dialog === "new" ? "Créer" : "Enregistrer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
